import { EventEmitter } from 'events';
import { uid } from 'uid';

import LoggerService from '../LoggerService';
import webSocketConnector from './WebSocketConnector';
import WebsocketActions from './WebsocketActions';

const MESSAGE_WAIT_SEC = 2;
const MILLIS_IN_SEC = 1000;
const DEFAULT_PING_TIMEOUT_MS = 15 * MILLIS_IN_SEC;

const log = (msg, ...args) => {
    LoggerService.log('WSService: ' + msg, ...args);
};

class WSService extends EventEmitter {
    actions = new Map();
    isInited = false;
    sendWaitTime = MESSAGE_WAIT_SEC * MILLIS_IN_SEC;
    pingInterval = DEFAULT_PING_TIMEOUT_MS;
    pingTimeout = null;
    isPingPongStart = false;
    _lastPingTimestamp = 0;
    socketDidDisconnect = false;
    lastCheckResult = {
        socketIsFine: true,
        resultTimestamp: Date.now()
    };

    get lastPingTimestamp() {
        return this._lastPingTimestamp;
    }

    constructor() {
        super();
        this.ws = webSocketConnector;
        this.ws.setOnClose(this.onClose);
        this.ws.registerMessageHandler(this.onMessageHandler);
    }

    send(channel, sendData = null) {
        log('send:init', { channel, sendData });
        if (!this.ws) {
            console.error('send:init', { channel, sendData });
            return null;
        }

        const id = uid(21);
        const wsData = {
            channel,
            request_id: id,
            ...(sendData ? { data: sendData } : {})
        };

        log('send:ws_send', { id, wsData });
        this.ws.send(wsData);
        return id;
    }

    onMessageHandler = (wsMessage) => {
        log('onMessageHandler:init', { wsMessage, class: this });
        const wsMessageData = wsMessage?.data;
        if (!wsMessageData) {
            console.error('WsApiService:onMessageHandler:empty_message', wsMessage);
            return;
        }

        try {
            const resp = JSON.parse(wsMessageData);
            const { response_id, channel, data, status } = resp;
            log('onMessageHandler: JSON', { data, channel, status });
            if (!data || !channel || !status || status?.code >= 400) {
                console.error('WsApiService: got message in wrong format');
                return;
            }

            if (response_id) {
                return this.emit(response_id, data);
            }

            const actionHandler = this.actions.get(channel);
            if (!this.actions.size || !actionHandler) {
                log('onMessageHandler:actions:not_find', { channel });
                return;
            }
            actionHandler(data);
        } catch (err) {
            log('onMessageHandler:error', err);
            console.error('WsApiService: Error handle message', err);
        }
    };

    async init() {
        log('init');
        if (this.isInited) {
            log('init:END 1');
            return;
        }

        this.isInited = true;
        WebsocketActions.init();
        this.addAction('connected', async () => {
            this.pingInterval = DEFAULT_PING_TIMEOUT_MS;
            this.isPingPongStart = true;
            this.tickPing();
            await WebsocketActions.onConnected();
        });
    }

    onClose = () => {
        this.stopPing();
    };

    tickPing() {
        this.clearPingTimeout();
        if (!this.isPingPongStart) {
            return;
        }
        this.pingTimeout = setTimeout(this.sendPing, this.pingInterval);
    }

    sendPing = () => {
        log('sendPing:init');
        return this.sendWait('ping').then(() => {
            log('sendPing:response');
            this._lastPingTimestamp = Date.now();
            if (this.socketDidDisconnect) {
                this.socketDidDisconnect = false;
                log('sendPing:response:start_reconnect');
                this.ws.reconnect();
            }

            log('sendPing:response:OK');
            this.tickPing();
        });
    };

    async checkIsSocketWorking() {
        log('checkIsSocketWorking:init');
        const notCorrectWsState = () => this.ws.readyState && this.ws.readyState > 1;
        const isCorrectInterval = () => {
            const timeDiff = Date.now() - this.lastPingTimestamp;
            return timeDiff < this.pingInterval * 1.1;
        };

        if (notCorrectWsState()) {
            log('checkIsSocketWorking:state:error:2');
            return false;
        }
        if (isCorrectInterval()) {
            log('checkIsSocketWorking:interval:OK:1');
            return true;
        }

        return new Promise((resolve) => {
            setTimeout(() => {
                if (notCorrectWsState()) {
                    log('checkIsSocketWorking:state:error:2');
                    return resolve(false);
                }
                if (isCorrectInterval()) {
                    log('checkIsSocketWorking:interval:OK:2');
                    return resolve(true);
                }

                log('checkIsSocketWorking:state:Timeout:ERROR');
                return resolve(false);
            }, this.pingInterval);
        });
    }

    async isSocketWorking() {
        log('isSocketWorking:init');
        const { resultTimestamp: timestampBefore } = this.lastCheckResult;
        if (Date.now() - timestampBefore < this.pingInterval / 2) {
            log('isSocketWorking:END_1');
            return null;
        }

        const result = await this.checkIsSocketWorking();
        const { resultTimestamp: timestampAfter } = this.lastCheckResult;
        if (Date.now() - timestampAfter < this.pingInterval / 2) {
            log('isSocketWorking:END_2');
            return null;
        }

        this.lastCheckResult = {
            resultTimestamp: Date.now(),
            socketIsFine: result
        };
        log('isSocketWorking:OK');
        return result;
    }

    async restartSocketIfNotWorking() {
        log('restartSocketIfNotWorking:init');
        const isWorking = await this.isSocketWorking();
        log('restartSocketIfNotWorking:status', { isWorking });
        if (isWorking === false) {
            this.socketDidDisconnect = true;
            this.ws.reconnect();
            log('restartSocketIfNotWorking:reconnect');
        }
    }

    clearPingTimeout() {
        log('clearPingTimeout:init');
        if (this.pingTimeout) {
            clearTimeout(this.pingTimeout);
        }
    }

    stopPing() {
        log('stopPing:init');
        this.isPingPongStart = false;
        this.clearPingTimeout();
    }

    addAction(channel, cb) {
        log('addAction:init', { channel, cb });
        this.actions.set(channel, cb);
    }

    sendWait(channel, sendData = {}) {
        log('sendWait:init', { channel, sendData });
        return new Promise((resolve, reject) => {
            const id = this.send(channel, sendData);
            if (!id) {
                log('sendWait:error:END_1', { channel, sendData });
                return reject(new Error('Failed to send message asynchronously'));
            }

            log('sendWait:add_listener', { channel, sendData });
            const timeout = setTimeout(reject, this.sendWaitTime);
            this.once(id, (data) => {
                clearTimeout(timeout);
                log('sendWait:response', { channel, sendData, response: data });
                resolve(data);
            });
        });
    }

    deleteAction(channel) {
        log('addAction:init', { channel });
        this.actions.delete(channel);
    }
}

export default new WSService();
